import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Label } from '@/app/components/ui/label';
import { LocationSearchComboBox } from '@/components/shared/location-search-combo-box';
import type { LocationOption } from '@/components/shared/location-search-combo-box';
import { JobSearchComboBox } from '@/components/shared/job-search-combo-box';
import type { JobOption } from '@/components/shared/job-search-combo-box';
import { useCreateRescueDrill } from '@/hooks/use-safety';
import { useOrgToday } from '@/hooks/use-settings';
import { PersonSearchComboBox } from '@/components/shared/person-search-combo-box';
import { OfflineIndicator } from '@/app/components/offline/offline-indicator';
import type { DrillOutcome } from '@/lib/types';

interface ParticipantRow {
  tech_id: number | null;
  name: string;
  role: string;
}

const outcomeOptions: { value: DrillOutcome; label: string; color: string }[] = [
  { value: 'successful', label: 'Successful', color: 'border-green-500 bg-green-50 text-green-700' },
  { value: 'partial', label: 'Partial', color: 'border-yellow-500 bg-yellow-50 text-yellow-700' },
  { value: 'failed', label: 'Failed', color: 'border-red-500 bg-red-50 text-red-700' },
];

export function CreateRescueDrillPage() {
  const navigate = useNavigate();
  const createMutation = useCreateRescueDrill();
  const orgToday = useOrgToday();

  const [date, setDate] = useState(orgToday);
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const [location, setLocation] = useState('');
  const [selectedProject, setSelectedProject] = useState<JobOption | null>(null);
  const [durationMinutes, setDurationMinutes] = useState('');
  const [participants] = useState('');
  const [participantRows, setParticipantRows] = useState<ParticipantRow[]>([]);
  const [scenarioDescription, setScenarioDescription] = useState('');
  const [outcome, setOutcome] = useState<DrillOutcome>('successful');
  const [lessonsLearned, setLessonsLearned] = useState('');
  const [nextDrillDue, setNextDrillDue] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const drill = await createMutation.mutateAsync({
        date,
        location_fk: selectedLocation?.id || null,
        location,
        project: selectedProject?.id,
        duration_minutes: durationMinutes ? parseInt(durationMinutes, 10) : undefined,
        participants: participants || undefined,
        participant_records: participantRows
          .filter((p) => p.name.trim())
          .map((p) => ({
            tech: p.tech_id ?? undefined,
            name: p.name,
            role: p.role || undefined,
          })),
        scenario_description: scenarioDescription,
        outcome,
        lessons_learned: lessonsLearned || undefined,
        next_drill_due: nextDrillDue || undefined,
      });
      navigate(`/safety/rescue-drills/${drill.id}`);
    } catch {
      // error handled by mutation
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/safety/rescue-drills')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Log Rescue Drill</h1>
          <p className="text-gray-600">Record a rescue drill exercise</p>
        </div>
      </div>

      <OfflineIndicator />

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Drill Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Location</Label>
                <LocationSearchComboBox value={selectedLocation} onChange={setSelectedLocation} />
              </div>
              <div>
                <Label htmlFor="location">Specific Area / Details</Label>
                <Input
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g. East face, 3rd floor scaffolding"
                />
              </div>
              <div>
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(e.target.value)}
                  min={1}
                />
              </div>
            </div>
            <div>
              <Label>Project</Label>
              <JobSearchComboBox
                value={selectedProject}
                onChange={(job) => {
                  setSelectedProject(job);
                  if (job?.location_fk_display && !selectedLocation) {
                    setSelectedLocation({
                      id: job.location_fk_display.id,
                      name: job.location_fk_display.name,
                      type: job.location_fk_display.type,
                    });
                  }
                }}
              />
            </div>
            <div>
              <Label htmlFor="scenario">Scenario Description *</Label>
              <Textarea
                id="scenario"
                value={scenarioDescription}
                onChange={(e) => setScenarioDescription(e.target.value)}
                rows={4}
                placeholder="Describe the rescue scenario practiced..."
                required
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Participants</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setParticipantRows([...participantRows, { tech_id: null, name: '', role: '' }])}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Participant
                </Button>
              </div>
              {participantRows.length === 0 ? (
                <p className="text-gray-500 text-center py-4 text-sm">
                  No participants added yet. Click &quot;Add Participant&quot; to begin.
                </p>
              ) : (
                <div className="space-y-3">
                  {participantRows.map((row, index) => (
                    <div key={index} className="flex items-start gap-3 border rounded-lg p-3">
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Name</Label>
                          <PersonSearchComboBox
                            endpoint="persons"
                            value={row.name ? { tech_id: row.tech_id, person_id: null, name: row.name } : null}
                            onChange={(person) => {
                              const updated = [...participantRows];
                              updated[index] = {
                                ...updated[index],
                                tech_id: person?.tech_id ?? null,
                                name: person?.name ?? '',
                              };
                              setParticipantRows(updated);
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Role</Label>
                          <Input
                            value={row.role}
                            onChange={(e) => {
                              const updated = [...participantRows];
                              updated[index] = { ...updated[index], role: e.target.value };
                              setParticipantRows(updated);
                            }}
                            placeholder="e.g., Rescuer"
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="mt-5"
                        onClick={() => setParticipantRows(participantRows.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Outcome</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              {outcomeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`border-2 rounded-lg p-4 text-center font-medium transition-all ${
                    outcome === option.value
                      ? option.color
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                  onClick={() => setOutcome(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="lessons">Lessons Learned</Label>
              <Textarea
                id="lessons"
                value={lessonsLearned}
                onChange={(e) => setLessonsLearned(e.target.value)}
                rows={3}
                placeholder="What was learned from this drill..."
              />
            </div>
            <div>
              <Label htmlFor="nextDrill">Next Drill Due</Label>
              <Input
                id="nextDrill"
                type="date"
                value={nextDrillDue}
                onChange={(e) => setNextDrillDue(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/safety/rescue-drills')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Saving...' : 'Save Drill'}
          </Button>
        </div>
      </form>
    </div>
  );
}
